import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui'
import { useUpdateBook } from '@/hooks/books'
import { type LibraryBook, stalledBooks } from '@/lib/insights'
import { captureError } from '@/lib/monitoring'
import { BookThumb, InsightCard } from './InsightCard'

const STALLED_AFTER_DAYS = 60

/** Books "reading" for too long, with a quick way to pause them */
export function StalledBooksCard({ books }: { books: LibraryBook[] }) {
  const { t } = useTranslation()
  const stalled = useMemo(() => stalledBooks(books, STALLED_AFTER_DAYS), [books])
  const updateBook = useUpdateBook()

  if (stalled.length === 0) return null

  const pause = async (bookId: string) => {
    try {
      // Back to the "to read" pile; clearing start_date so a restart counts fresh
      await updateBook.mutateAsync({ bookId, data: { status: 'unread', start_date: null } })
    } catch (error) {
      captureError(error as Error, { context: 'Failed to pause stalled book' })
    }
  }

  return (
    <InsightCard
      icon="⏸️"
      title={t('insights.stalled.title')}
      description={t('insights.stalled.description', { count: STALLED_AFTER_DAYS })}
    >
      <ul className="space-y-3">
        {stalled.map(({ book, daysReading }) => (
          <li key={book.id} className="flex items-center gap-3">
            <BookThumb src={book.cover_url} title={book.title} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{book.title}</p>
              <p className="text-xs text-muted-foreground">
                {t('insights.continueReading.daysReading', { count: daysReading ?? 0 })}
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={updateBook.isPending}
              onClick={() => pause(book.id)}
              title={t('insights.stalled.pauseHint')}
            >
              {t('insights.stalled.pause')}
            </Button>
          </li>
        ))}
      </ul>
    </InsightCard>
  )
}
